/**
 * Bandeau d'ambiance des accueils (31/10/2026) : une photo par rôle, pour que chacun s'identifie — formateur en
 * animation, apprenants en formation, conseillère formation en rendez-vous. Visuels repris de la vitrine
 * (`vitrine/public/images`, copies recadrées en WebP ≤ 150 Ko dans `public/accueil/`), voile aux couleurs de la marque.
 * Discret : il s'ajoute au-dessus des accueils existants sans en changer la structure.
 */
import { cx } from "./base";
import { Avatar } from "./Avatar";
import { useMoi } from "../session";

export const BANDEAUX = {
  formateur: {
    image: "/accueil/formateur.webp",
    alt: "Une formatrice anime une séance devant un tableau blanc, face à un groupe de professionnels attentifs",
    titre: "Espace formateur",
    phrase: "Transmettez votre savoir-faire : le cadre Qualiopi, les conventions et le suivi sont portés pour vous.",
  },
  admin: {
    image: "/accueil/conseil.webp",
    alt: "Une conseillère formation, tablette en main, échange avec un interlocuteur lors d'un rendez-vous",
    titre: "Conseil en formation",
    phrase: "Chaque dossier, de la convention au paiement, et les publications du catalogue.",
  },
  apprenant: {
    image: "/accueil/apprenants.webp",
    alt: "Des apprenants souriants suivent une formation en salle, ordinateurs et carnets ouverts",
    titre: "Espace apprenant",
    phrase: "Votre formation, vos documents à signer et vos supports, au même endroit.",
  },
} as const;

export type RoleBandeau = keyof typeof BANDEAUX;

export function BandeauRole({ role, className }: { role: RoleBandeau; className?: string }) {
  const b = BANDEAUX[role];
  const moi = useMoi();
  const acteur = moi.data?.acteur;
  return (
    <figure className={cx("relative mb-6 h-28 overflow-hidden rounded-lg shadow-carte sm:h-36", className)} data-bandeau={role}>
      <img src={b.image} alt={b.alt} width={1440} height={440} decoding="async" className="absolute inset-0 size-full object-cover object-[50%_35%]" />
      {/* Voile de marque : encre verte à gauche (lisibilité du texte), transparent à droite (la photo respire). */}
      <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-encre/85 via-accent-fort/55 to-transparent" />
      <figcaption className="relative flex h-full max-w-[34rem] flex-col justify-center px-5 sm:px-7">
        <span className="text-[11px] font-semibold tracking-[0.14em] text-attente uppercase">{b.titre}</span>
        <span className="mt-1 font-display text-[15px] leading-snug font-medium text-sur-accent sm:text-[17px]">{b.phrase}</span>
      </figcaption>
      {/* Photo de profil (ou initiales) de la personne connectée, à droite du bandeau (à partir de 640 px ; sur téléphone, elle est dans l’en-tête). */}
      {acteur && (
        <span className="absolute top-1/2 right-6 hidden -translate-y-1/2 rounded-full ring-2 ring-carte/90 sm:block">
          <Avatar nom={acteur.nom} photo={moi.data?.photo ?? null} taille="lg" />
        </span>
      )}
    </figure>
  );
}
